import React, { useState, useEffect, ReactElement, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../layout/Layout';
import Pagination from '../../common/Pagination';
import {
  UserDisplay,
  UserResponse,
  ManagerInfo,
  mapApiUserToDisplayUser,
  getManagers,
  mapDisplayUserToApiUpdate,
  mapDisplayUserToApiCreate,
  testMapApiUsers,
  deleteUser
} from '../../../services/users';
import { useAuth } from '../../../context/AuthContext';
import apiClient from '../../../services/apiClient';
import { isAdmin } from '../../../utils/roleUtils';
import roleService, { Role } from '../../../services/roleService';
import { ENDPOINTS, API_URL } from '../../../config';
import storeTypeService, { StoreType } from '../../../services/storeTypeService';
import {
  PageContainer,
  PageHeaderComponent,
  SearchContainer,
  SearchField,
  AddButtonComponent,
  BackButtonComponent,
  SearchInputComponent
} from '../../ui/DesignSystem';
import UserManagementTable from '../UserManagementTable';
import { getUsers } from '../../../services/users';
import UserEditIcon from '../../../assets/UserEditIcon.png';
import userDeleteIcon from '../../../assets/userDeleteIcon.png';
import PageLoadingSpinner from '../../ui/PageLoadingSpinner';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../common/Toast';

const GridContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e0e0e0;
`;

const GridHeader = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 0.9fr 1.3fr 0.7fr 0.9fr 0.9fr 0.7fr 0.7fr;
  background-color: #042B41;
  color: white;

  div {
    padding: 10px 12px;
    font-weight: 500;
    font-size: 14px;
    height: 48px;
    display: flex;
    align-items: center;
    vertical-align: middle;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 0.9fr 1.3fr 0.7fr 0.9fr 0.9fr 0.7fr 0.7fr;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s;
  height: 48px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9fafb;
  }

  div {
    padding: 10px 12px;
    color: #333;
    display: flex;
    align-items: center;
    border-right: 1px solid #e0e0e0;
    overflow: hidden;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
    font-size: 14px;
    height: 48px;
    box-sizing: border-box;

    &:last-child {
      border-right: none;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;

  button {
    background: none;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 3px;
    transition: all 0.2s;

    img {
      width: 18px;
      height: 18px;
    }

    &.edit {
      color: #042B41;

      &:hover {
        background-color: #f0f8ff;
        border-color: #c0d5e8;
        transform: translateY(-1px);
      }
    }

    &.delete {
      color: #dc3545;

      &:hover {
        background-color: #fff5f5;
        border-color: #ffccd1;
        transform: translateY(-1px);
      }
    }

    &:disabled {
      color: #ccc;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 10px;
  width: 850px;
  max-width: 90%;
  padding: 2.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  margin: 0 0 1.75rem 0;
  color: #333;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.75rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 500;
  color: #333;

  .required {
    color: #dc3545;
    margin-left: 2px;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.85rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }

  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.85rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.95rem;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.85rem center;
  background-size: 12px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }

  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.85rem 1.75rem;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d0d0d0;
  }

  &:disabled {
    background-color: #f9f9f9;
    color: #999;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.85rem 1.75rem;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #c82333;
  }

  &:disabled {
    background-color: #f9f9f9;
    color: #999;
    cursor: not-allowed;
  }
`;

const DeleteModalContent = styled(ModalContent)`
  width: 500px;
  max-width: 90%;
  padding: 2rem;
`;

const DeleteModalText = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: #333;
  line-height: 1.5;
`;

const SubmitButton = styled.button`
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.85rem 1.75rem;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 130px;

  svg {
    margin-right: 0.5rem;
  }

  &:hover {
    background-color: #031f30;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #fff5f5;
  border: 1px solid #ffccd1;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  white-space: pre-line;
  line-height: 1.4;
  font-size: 0.9rem;

  svg {
    margin-right: 0.75rem;
    min-width: 16px;
    margin-top: 0.1rem;
    flex-shrink: 0;
  }

  /* Ensure error text wraps properly and maintains readability */
  & > span {
    flex: 1;
  }
`;

const FieldErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  line-height: 1.3;
  
  /* Style each error as a separate line */
  & > div {
    margin-bottom: 0.15rem;
  }
  
  & > div:last-child {
    margin-bottom: 0;
  }
`;

// Add styled components for password input with eye icon
const PasswordContainer = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #042B41;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  
  &:disabled {
    color: #9CA3AF;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0.5rem;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #042B41;
  }
  
  label {
    font-size: 0.9rem;
    color: #333;
    cursor: pointer;
    user-select: none;
  }
`;

// Add interface for form data
interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: string;
  status: string;
  mobile: string;
  storeTypeId: number | null;
  managerId: string | null;
  canAccessAllStores: boolean;
}

// Add interface for Manager
interface Manager {
  id: string;
  user_name: string;
}

// Add an enhanced manager interface that includes store type
interface EnhancedManager extends Manager {
  store_type_id?: number;
  store_type?: {
    id: number;
    name: string;
  };
}

// Simplified - direct string role rather than object with ID
interface RoleData {
  value: string;
  label: string;
}

// Add skeleton loader and empty state components
const LoadingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  height: 200px;
`;

const LoadingContainer = styled.div`
  grid-column: 1 / 9;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
`;

// Add this styled component before the UsersPage component definition
const ActionButton = styled.button`
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 22px;
    height: 22px;
  }
  
  &:hover {
    background-color: #F3F4F6;
    color: #042B41;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Interface for API error responses
interface ApiError {
  response?: {
    status: number;
    headers: any;
    data: any;
  };
  message?: string;
  code?: string;
  status?: number; // Add status property to match ApiError class
  isAuthError?: boolean; // Add isAuthError property to match ApiError class
}

// Add AddButton styled component
const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    background-color: #0A3D5A;
  }
  
  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }
`;

// Completely redefine the layout components for proper alignment
const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 10px;
`;

// Add a wrapper for the back button with proper margin
const BackButtonWrapper = styled.div`
  margin-right: 12px;
`;

// Custom header with proper alignment
const CustomHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  margin-top: 10px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  padding: 0;
  color: #333;
`;

// Create a single container for search and actions that's properly aligned with the table
const SearchActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  width: 100%;
  gap: 16px;
`;

// Create a styled wrapper for the SearchInputComponent
const StyledSearchInput = styled.div`
  flex: 1;
  max-width: 500px;
  
  /* Make sure the input inside takes full width */
  input {
    width: 100%;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  grid-column: 1 / 9;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  svg {
    width: 30px;
    height: 30px;
    color: #9ca3af;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.2rem;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const EmptyStateDescription = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  max-width: 300px;
  margin-bottom: 1.5rem;
`;

const EmptyStateButton = styled.button`
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #0A3D5A;
  }
`;

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user: currentAuthUser } = useAuth();
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Keep ref in sync with state
  useEffect(() => {
    itemsPerPageRef.current = itemsPerPage;
  }, [itemsPerPage]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [users, setUsers] = useState<UserDisplay[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [managers, setManagers] = useState<EnhancedManager[]>([]);
  const [storeTypes, setStoreTypes] = useState<StoreType[]>([]);
  const [currentUser, setCurrentUser] = useState<UserDisplay | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Replace hardcoded roles with state that will be populated from API
  const [roles, setRoles] = useState<RoleData[]>([
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'staff', label: 'Staff' }
  ]);

  // Add state for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<{id: string, name: string} | null>(null);
  
  // Add state for delete error modal
  const [showDeleteErrorModal, setShowDeleteErrorModal] = useState<boolean>(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string>('');
  
  // Add dataLoaded flag to track if data was ever successfully loaded
  const [dataLoaded, setDataLoaded] = useState(false);
  const [fetchingInProgress, setFetchingInProgress] = useState(false);
  const [loadingManagersAndStoreTypes, setLoadingManagersAndStoreTypes] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [isPageChanging, setIsPageChanging] = useState(false);

  // Add ref for the search input to maintain focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Add ref to track previous search query
  const previousSearchRef = useRef<string>('');

  // Check if current user has admin role using the utility function
  const userIsAdmin = isAdmin(currentAuthUser);

  // Individual field error states for inline validation
  const [fieldErrors, setFieldErrors] = useState<{
    password: string[];
    email: string[];
    mobile: string[];
  }>({
    password: [],
    email: [],
    mobile: []
  });

  // State to track original form data and changes for Edit User modal
  const [originalFormData, setOriginalFormData] = useState<UserFormData | null>(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  // Add password visibility state for both Add and Edit modals
  const [showPassword, setShowPassword] = useState(false);

  // Create a ref to store the current AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  // Create a ref to store the current itemsPerPage to avoid closure issues
  const itemsPerPageRef = useRef<number>(10);

  // No longer needed as we're using direct API calls
  // const directFetchInProgressRef = useRef(false);

  // Debounce search query to prevent excessive API calls and focus loss
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Increased delay to reduce API calls

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to first page when debounced search query changes
  useEffect(() => {
    if (dataLoaded) {
      // Only reset to page 1 if we're searching and not already on page 1
      if (debouncedSearchQuery.trim() && currentPage !== 1) {
        console.log('Resetting to page 1 due to search');
        setCurrentPage(1);
      }
      // Reset to page 1 if search was cleared and we're not on page 1
      else if (!debouncedSearchQuery.trim() && currentPage !== 1) {
        console.log('Resetting to page 1 due to cleared search');
        setCurrentPage(1);
      }
    }
  }, [debouncedSearchQuery, dataLoaded]); // Removed currentPage from dependencies to prevent infinite loops

  // Track when currentAuthUser becomes available to ensure proper delete button visibility
  const [authUserLoaded, setAuthUserLoaded] = useState(false);
  
  useEffect(() => {
    // Track when currentAuthUser becomes available
    if (currentAuthUser && !authUserLoaded) {
      setAuthUserLoaded(true);
      console.log('Current auth user loaded, delete button visibility will be updated');
    } else if (!currentAuthUser && authUserLoaded) {
      setAuthUserLoaded(false);
    }
  }, [currentAuthUser, authUserLoaded]);

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    role: '',
    status: 'active',
    mobile: '',
    storeTypeId: null,
    managerId: null,
    canAccessAllStores: false
  });


  // Load managers and store types
  const loadManagersAndStoreTypes = async () => {
    setLoadingManagersAndStoreTypes(true);
    try {
      console.log('Loading managers and store types...');

      // Load managers
      const managersData = await getManagers();
      console.log(`Loaded ${managersData.length} managers`);
      setManagers(managersData);

      // Load store types
      const storeTypesData = await storeTypeService.getStoreTypes();
      setStoreTypes(storeTypesData);

      console.log('Finished loading managers and store types');
    } catch (error) {
      console.error('Error loading managers and store types:', error);
      setError('Failed to load managers and store types. Please try again.');
    } finally {
      setLoadingManagersAndStoreTypes(false);
    }
  };

  // Fetch users from API
  const fetchUsers = async (options?: { overrideSearch?: string }) => {
    // If we're already loading, fetching in progress, or page is changing, don't trigger another load
    if (isLoading || fetchingInProgress || isPageChanging) {
      console.log('Skipping fetchUsers - already loading, fetching in progress, or page changing');
      return;
    }

    console.log('fetchUsers called with:', { 
      currentPage, 
      itemsPerPage, 
      debouncedSearchQuery: debouncedSearchQuery.trim(),
      originalSearchQuery: searchQuery 
    });

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setFetchingInProgress(true);
    setIsLoading(true);
    setError(null); // Clear previous errors

    try {
      // Determine search term first (allow an override for initial URL-driven loads)
      const searchTerm = (options?.overrideSearch ?? debouncedSearchQuery ?? searchQuery).trim();
      const isSearching = searchTerm.length > 0;

      // If searching, always fetch from first page to avoid empty views when previous page > 1
      const effectivePage = isSearching ? 1 : currentPage;
      // Use ref to get the latest itemsPerPage value to avoid closure issues
      const currentItemsPerPage = itemsPerPageRef.current;
      // Calculate skip from effectivePage and currentItemsPerPage
      const skip = (effectivePage - 1) * currentItemsPerPage;

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('skip', skip.toString());
      queryParams.append('limit', currentItemsPerPage.toString());
      
      // Only add search parameter if there's actually a search term
      // When invoked immediately after setting from URL, debounced value may not be set yet.
      // Fall back to current searchQuery to avoid missing the filter on first load from Global Search.
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }

      const apiUrl = `${API_URL}${ENDPOINTS.USERS}?${queryParams.toString()}`;
      console.log('Making API request to URL:', apiUrl);
      console.log('API call parameters:', {
        skip,
        limit: currentItemsPerPage,
        search: searchTerm || 'none',
        url: apiUrl
      });

      // Direct API call to ensure we get the correct response format
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found in localStorage');
        throw new Error('Authentication token is missing');
      }

      console.log('Using auth token:', token ? `${token.substring(0, 10)}...` : 'missing');
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Intercepted-Request': 'true' // Add this to avoid recursion in interceptor
        },
        signal: abortController.signal
      });

      // Check if this request was aborted
      if (abortController.signal.aborted) {
        console.log('Request was aborted, ignoring response');
        return;
      }

      console.log('API response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('API response data:', data);
      console.log('API response:', { 
        itemsCount: data.items?.length || 0, 
        total: data.total,
        page: data.page,
        pages: data.pages,
        hasItems: !!data.items,
        searchTerm: searchTerm || 'none'
      });

      if (data && data.items && Array.isArray(data.items)) {
        // Directly map the API response to our display format
        const displayUsers = data.items.map((item: any) => {
          const mappedUser = {
            id: String(item.id || ''),
            name: String(item.user_name || ''),
            email: String(item.email || ''),
            role: String(item.role || 'staff'),
            status: item.is_active === false ? 'inactive' : 'active',
            mobile: String(item.mobile_number || ''),
            storeType: item.store_type?.name || '',
            storeTypeId: item.store_type?.id || null,
            manager: item.manager?.user_name || '',
            managerId: item.manager?.id || null,
            canAccessAllStores: item.can_access_all_stores === true,
          };
          
          return mappedUser;
        });

        console.log('Setting users:', displayUsers.length, 'total:', data.total);
        setUsers(displayUsers);
        setTotalUsers(data.total || displayUsers.length);
        setTotalPages(data.pages || Math.ceil(data.total / currentItemsPerPage) || 1);
        setDataLoaded(true);

        // If we forced page 1 for a search, make sure UI reflects that
        if (isSearching && currentPage !== 1) {
          setCurrentPage(1);
        }
      } else {
        console.log('No users data received, setting empty array');
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(1);
      }
    } catch (err) {
      // Ignore AbortError as it's expected when cancelling requests
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Request was aborted:', err.message);
        return;
      }
      
      console.error('Error fetching users:', err);
      
      // Provide more detailed error message
      let errorMessage = 'Failed to load users. Please try again.';
      if (err instanceof Error) {
        errorMessage = `Error: ${err.message}`;
        console.error('Error details:', err.stack);
      }
      
      setError(errorMessage);

      if (!dataLoaded) {
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(1);
      }
    } finally {
      // Only clear loading state if this is still the current request
      if (abortControllerRef.current === abortController) {
        setIsLoading(false);
        setFetchingInProgress(false);
        abortControllerRef.current = null;
      }
    }
  };

  // Helper function to reset form data to initial state
  const resetFormData = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: '',
      status: 'active',
      mobile: '',
      storeTypeId: null,
      managerId: null,
      canAccessAllStores: false
    });
    setError(null);
    setFieldErrors({
      password: [],
      email: [],
      mobile: []
    });
    setOriginalFormData(null);
    setHasFormChanges(false);
    // Reset password visibility state
    setShowPassword(false);
  };

  // Validation functions for inline error messaging
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter (A-Z)');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter (a-z)');
    }

    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number (0-9)');
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(password)) {
      errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
    }

    return errors;
  };

  const validateEmail = (email: string): string[] => {
    const errors: string[] = [];

    if (!email) {
      errors.push('Email is required');
      return errors;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.push('Please enter a valid email address');
    }

    return errors;
  };

  const validateMobile = (mobile: string): string[] => {
    const errors: string[] = [];

    if (!mobile) {
      errors.push('Mobile number is required');
      return errors;
    }

    const mobileRegex = /^(\+61\d{8,9}|0\d{8,9})$/;
    if (!mobileRegex.test(mobile)) {
      errors.push('Please enter a valid Australian mobile number (+61xxxxxxxx or 0xxxxxxxxx)');
    }

    return errors;
  };

  // Function to handle mobile number input restrictions (only numbers and + allowed)
  const handleMobileKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = e.key;
    const currentValue = (e.target as HTMLInputElement).value;

    // Allow backspace, delete, tab, escape, enter, and arrow keys
    if (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'Escape' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown'
    ) {
      return;
    }

    // Allow + only at the beginning and only one +
    if (char === '+') {
      if (currentValue.length === 0 && !currentValue.includes('+')) {
        return; // Allow + at the beginning
      } else {
        e.preventDefault(); // Prevent + if not at beginning or already exists
        return;
      }
    }

    // Allow only numbers (0-9)
    if (!/[0-9]/.test(char)) {
      e.preventDefault();
    }
  };

  // Function to check if form has changes from original data
  const checkForFormChanges = (currentData: UserFormData, originalData: UserFormData | null): boolean => {
    if (!originalData) return false;

    // Compare all relevant fields (excluding password as it's optional in edit mode)
    return (
      currentData.name !== originalData.name ||
      currentData.email !== originalData.email ||
      currentData.mobile !== originalData.mobile ||
      currentData.role !== originalData.role ||
      currentData.status !== originalData.status ||
      currentData.storeTypeId !== originalData.storeTypeId ||
      currentData.managerId !== originalData.managerId ||
      currentData.canAccessAllStores !== originalData.canAccessAllStores ||
      Boolean(currentData.password && currentData.password.length > 0) // Password change if not empty
    );
  };

  // Track form changes for Edit User modal
  useEffect(() => {
    if (currentUser && originalFormData) {
      const hasChanges = checkForFormChanges(formData, originalFormData);
      setHasFormChanges(hasChanges);
    } else {
      setHasFormChanges(false);
    }
  }, [formData, originalFormData, currentUser]);

  // Helper function to render field errors
  const renderFieldErrors = (fieldName: 'password' | 'email' | 'mobile') => {
    const errors = fieldErrors[fieldName];
    if (!errors || errors.length === 0) return null;

    return (
      <FieldErrorMessage>
        {errors.map((error, index) => (
          <div key={index}>{error}</div>
        ))}
      </FieldErrorMessage>
    );
  };

  // Function to validate all fields and return combined errors
  const validateAllFields = (): boolean => {
    const emailErrors = validateEmail(formData.email);
    const mobileErrors = validateMobile(formData.mobile);

    // Password validation only required for new users (Add mode)
    let passwordErrors: string[] = [];
    if (!currentUser && formData.password) {
      passwordErrors = validatePassword(formData.password);
    } else if (!currentUser && !formData.password) {
      passwordErrors = ['Password is required'];
    }

    // Update all field errors at once
    setFieldErrors({
      password: passwordErrors,
      email: emailErrors,
      mobile: mobileErrors
    });

    // Check basic required fields
    if (!formData.name || !formData.email || (!currentUser && !formData.password) || !formData.role) {
      setError("Please fill in all required fields.");
      return false;
    }

    // Role-specific validations
    if (formData.role === 'staff' && !formData.managerId) {
      setError("Manager is required for users with Staff role.");
      return false;
    }

    // Store type is mandatory for managers
    if (formData.role === 'manager' && !formData.storeTypeId) {
      setError("Store type is required for users with Manager role.");
      return false;
    }

    // For staff users, check if store type can be determined from manager
    if (formData.role === 'staff' && formData.managerId && !formData.storeTypeId) {
      const selectedManager = managers.find(m => m.id === formData.managerId);
      if (selectedManager && selectedManager.store_type_id !== undefined) {
        // Update form data with manager's store type
        setFormData(prev => ({
          ...prev,
          storeTypeId: selectedManager.store_type_id!
        }));
      }
    }

    // Return true if no field errors and no global validation issues
    return emailErrors.length === 0 && mobileErrors.length === 0 && passwordErrors.length === 0;
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleAddUser = async () => {
    // Check if user has admin role
    if (!userIsAdmin) {
      setError("You don't have permission to add users. Only administrators can add users.");
      return;
    }

    // Reset form data first to ensure clean state
    console.log('Resetting form data for Add User modal');
    resetFormData();

    // Load managers and store types if not already loaded
    if (managers.length === 0 || storeTypes.length === 0) {
      await loadManagersAndStoreTypes();
    }

    setShowAddModal(true);
  };

  const handleEditUser = async (user: UserDisplay) => {
    console.log('handleEditUser called with user:', user);
    
    // Reset password visibility state
    setShowPassword(false);
    
    // Set current user for editing
    setCurrentUser(user);
    
    // Prepare form data from user data
    const editFormData: UserFormData = {
      name: user.name,
      email: user.email,
      password: '', // Always empty for editing
      role: user.role,
      status: user.status,
      mobile: user.mobile,
      storeTypeId: user.storeTypeId || null, // Use storeTypeId directly instead of user.store_type?.id
      managerId: user.managerId || null,
      canAccessAllStores: user.canAccessAllStores === true
    };
    
    console.log('Setting form data for edit:', editFormData);
    console.log('User store type info:', { 
      storeTypeId: user.storeTypeId, 
      storeType: user.storeType,
      store_type: user.store_type 
    });
    console.log('User canAccessAllStores:', user.canAccessAllStores, 'Form canAccessAllStores:', editFormData.canAccessAllStores);
    setFormData(editFormData);
    
    // Store original data for change detection
    setOriginalFormData(editFormData);
    
    // Clear any existing errors
    setError(null);
    setFieldErrors({
      password: [],
      email: [],
      mobile: []
    });
    
    // Load managers and store types if not already loaded to ensure dropdown is populated
    if (managers.length === 0 || storeTypes.length === 0) {
      await loadManagersAndStoreTypes();
    }
    
    setShowEditModal(true);
  };

  const handleDeleteUser = async (id: string) => {
    // Check if user has admin role
    if (!userIsAdmin) {
      setError("You don't have permission to delete users. Only administrators can delete users.");
      return;
    }

    // Prevent users from deleting their own account
    if (currentAuthUser && currentAuthUser.id === id) {
      setError("You cannot delete your own account. Please ask another administrator to deactivate your account if needed.");
      return;
    }

    // Find the user to delete to show their name in the confirmation dialog
    const userToDeleteObj = users.find(user => user.id === id);
    if (!userToDeleteObj) {
      setError("User not found.");
      return;
    }

    // Show delete confirmation modal
    setUserToDelete({
      id,
      name: userToDeleteObj.name || 'Unknown User'
    });
    setShowDeleteModal(true);
  };

  // Add new function to handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    const id = userToDelete.id;
    const userName = userToDelete.name;
    
    setIsLoading(true);
    setShowDeleteModal(false); // Hide the modal
    
    try {
      // Check if the user is a manager for toast message
      const userObj = users.find(user => user.id === id);
      const wasManager = userObj?.role === 'manager';

      // Call the proper DELETE API endpoint to actually delete the user
      await deleteUser(id);

      // If the deleted user was a manager, refresh the managers list so they disappear from dropdowns immediately
      if (wasManager) {
        console.log('Manager deleted, refreshing managers list...');
        try {
          const updatedManagersData = await getManagers();
          setManagers(updatedManagersData);
          console.log(`Managers list refreshed, now contains ${updatedManagersData.length} managers`);
        } catch (managerRefreshError) {
          console.error('Error refreshing managers list:', managerRefreshError);
          // Don't show error to user since the main operation succeeded
        }
      }

      // Show success toast
      toast.showToast(`User "${userName}" has been deleted successfully!`, { type: 'success' });

      // Calculate pagination logic
      const currentPageUserCount = users.length;
      const isLastUserOnPage = currentPageUserCount === 1;
      const isNotFirstPage = currentPage > 1;
      const newTotalUsers = totalUsers - 1;

      // Update total users count first
      setTotalUsers(newTotalUsers);

      // If this is the last user on a page that's not the first page, go to previous page
      if (isLastUserOnPage && isNotFirstPage) {
        console.log(`Last user on page ${currentPage}, navigating to page ${currentPage - 1}`);
        setCurrentPage(currentPage - 1);
        // The useEffect will automatically trigger fetchUsers when currentPage changes
      } else {
        // Refresh data immediately if staying on the same page
        await fetchUsers();
      }

      setError(null); // Clear any previous errors
      setUserToDelete(null); // Reset user to delete
    } catch (error) {
      console.error('Error deleting user:', error);
      
      // Handle specific error from API when manager has staff members
      let errorMessage = 'Failed to delete user. Please try again.';
      
      if (error && typeof error === 'object') {
        const apiError = error as ApiError;
        
        // Check if it's a 400 error with manager has staff members message
        if (apiError.response?.status === 400 && apiError.response?.data?.detail) {
          const detail = apiError.response.data.detail;
          
          // If the error mentions manager with staff members, use the detailed message
          if (detail.includes('staff member') || detail.includes('reporting to them')) {
            errorMessage = detail;
          }
        }
        // Fallback for other error formats
        else if (apiError.message) {
          errorMessage = apiError.message;
        }
      }
      
      // Show error in a modal instead of page-level error
      setDeleteErrorMessage(errorMessage);
      setShowDeleteErrorModal(true);
      
      // Clear any page-level error since we're showing it in a modal
      setError(null);
      
      setUserToDelete(null); // Reset user to delete on error too
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Store the original form update logic
    let shouldUpdateForm = true;
    let formUpdate: Partial<UserFormData> = { [name]: value };

    // If changing the role, apply role-specific requirements
    if (name === 'role') {
      if (value === 'manager') {
        // Manager: Store type is mandatory, no manager
        formUpdate = {
          ...formUpdate,
          managerId: null // Managers don't have a manager
        };
      }
      else if (value === 'admin') {
        // Admin: Store type is optional
        // Don't modify storeTypeId for admin - it's optional
      }
      else if (value === 'staff') {
        // Staff: Manager is mandatory, store type should auto-populate from manager
        // If a manager is already selected, get their store type
        const currentManagerId = formData.managerId;
        if (currentManagerId) {
          const selectedManager = managers.find(m => m.id === currentManagerId);
          if (selectedManager) {
            // Check for store_type object first
            if (selectedManager.store_type && selectedManager.store_type.id !== undefined) {
              // Update with manager's store type from store_type object
              const storeTypeId = selectedManager.store_type.id;
              formUpdate = {
                ...formUpdate,
                storeTypeId: storeTypeId
              };
              console.log(`Auto-populated store type from existing manager: ${storeTypeId}`);
            }
          }
        }

        // Default case for role change
        setFormData(prev => ({
          ...prev,
          ...formUpdate
        }));
        shouldUpdateForm = false;
      }
      else {
        // Default case
        setFormData(prev => ({
          ...prev,
          ...formUpdate
        }));
        shouldUpdateForm = false;
      }
    }
    // If changing manager, auto-populate store type for staff role and handle manager selection for all roles
    else if (name === 'managerId') {
      // Convert empty value to null
      const managerId = value === '' ? null : value;
      console.log(`Manager selected: ${managerId}, user role: ${formData.role}`);

      // Update form data with the manager ID
      if (managerId === null) {
        // If manager was cleared, clear store type for staff role
        if (formData.role === 'staff') {
          setFormData(prev => ({
            ...prev,
            managerId: null,
            storeTypeId: null // Clear store type when manager is cleared for staff
          }));
          console.log("Manager cleared, also clearing store type for staff role");
        } else {
          // For other roles, just clear manager
          setFormData(prev => ({
            ...prev,
            managerId: null
          }));
        }
        shouldUpdateForm = false;
      } else {
        // Find selected manager
        const selectedManager = managers.find(manager => manager.id === managerId);
        console.log("Selected manager:", selectedManager);

        // For staff role, auto-populate store type if available
        if (formData.role === 'staff' && selectedManager) {
          if (selectedManager.store_type && selectedManager.store_type.id !== undefined) {
            const storeTypeId = selectedManager.store_type.id;
            setFormData(prev => ({
              ...prev,
              managerId: managerId,
              storeTypeId: storeTypeId
            }));
            console.log(`Auto-populated store type ${storeTypeId} from manager ${selectedManager.user_name}`);
            shouldUpdateForm = false;
          }
        } else {
          // For other roles or if no store type found, just update manager
          setFormData(prev => ({
            ...prev,
            managerId: managerId
          }));
          shouldUpdateForm = false;
        }
      }
    }

    // Default form update if not handled above
    if (shouldUpdateForm) {
      // Handle storeTypeId conversion
      if (name === 'storeTypeId') {
        setFormData(prev => ({
          ...prev,
          storeTypeId: value === '' ? null : parseInt(value, 10)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          ...formUpdate
        }));
      }
    }

    // Perform real-time validation for specific fields
    if (name === 'password' && value) {
      const passwordErrors = validatePassword(value);
      setFieldErrors(prev => ({
        ...prev,
        password: passwordErrors
      }));
    } else if (name === 'password' && !value) {
      // Clear password errors when field is empty (for edit mode where password is optional)
      setFieldErrors(prev => ({
        ...prev,
        password: []
      }));
    }

    if (name === 'email') {
      const emailErrors = validateEmail(value);
      setFieldErrors(prev => ({
        ...prev,
        email: emailErrors
      }));
    }

    if (name === 'mobile') {
      const mobileErrors = validateMobile(value);
      setFieldErrors(prev => ({
        ...prev,
        mobile: mobileErrors
      }));
    }

    // Check for form changes after any input change (for Edit User modal)
    if (currentUser && originalFormData) {
      // Use a timeout to ensure state updates are processed
      setTimeout(() => {
        const currentFormData = formData;
        // Update the current field value in the comparison
        const updatedFormData = { ...currentFormData, [name]: value };
        const hasChanges = checkForFormChanges(updatedFormData, originalFormData);
        setHasFormChanges(hasChanges);
      }, 0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if user has admin role for creating users
    if (!currentUser && !userIsAdmin) {
      setError("You don't have permission to add users. Only administrators can add users.");
      return;
    }

    // Validate all fields before submission
    const isValid = validateAllFields();
    if (!isValid) {
      // Field errors are already set by validateAllFields, don't set a global error
      return;
    }

    setIsLoading(true);
    setError(null); // Clear any global errors

    try {
      if (currentUser) {
        // Update existing user
        const apiData: Record<string, any> = {
          user_name: formData.name,
          email: formData.email,
          mobile_number: formData.mobile,
          is_active: formData.status === 'active'
        };

        // Send the role string directly instead of looking up a role ID
        apiData.role = formData.role;

        // Only include password if changed
        if (formData.password) {
          apiData.password = formData.password;
        }

        // Role-specific fields - instead of manual mapping, use the helper function
        const updateData = mapDisplayUserToApiUpdate({
          name: formData.name,
          email: formData.email,
          password: formData.password || undefined,
          role: formData.role,
          status: formData.status,
          mobile: formData.mobile,
          storeTypeId: formData.storeTypeId,
          managerId: formData.managerId,
          canAccessAllStores: formData.canAccessAllStores
        }, managers);

        // Copy the properties from updateData to apiData
        Object.assign(apiData, {
          store_type_id: updateData.store_type_id,
          manager_id: updateData.manager_id,
          can_access_all_stores: updateData.can_access_all_stores
        });

        console.log('Sending user update data:', JSON.stringify(apiData, null, 2));

        // Make API call to update user
        const updatedUserResponse = await apiClient.put<UserResponse>(`/api/v1/users/${currentUser.id}`, apiData);

        console.log('API response:', updatedUserResponse);

        if (updatedUserResponse) {
          let updatedUser: UserDisplay;
          try {
            updatedUser = mapApiUserToDisplayUser(updatedUserResponse);
          } catch (mappingError) {
            console.error('Error mapping updated user:', mappingError);
            // Create a fallback user object with form data
            updatedUser = {
              ...currentUser,
              name: formData.name,
              email: formData.email,
              role: formData.role,
              status: formData.status,
              mobile: formData.mobile,
              storeType: apiData.store_type_id === 1 ? 'Cranbourne' :
                (apiData.store_type_id === 2 ? 'Sale' : 'N/A'),
              storeTypeId: apiData.store_type_id,
              managerId: apiData.manager_id
            };
          }

          // Update users array with the updated user
          setUsers(users.map(user => user.id === updatedUser.id ? updatedUser : user));

          // If the user's role was changed to manager, refresh the managers list so they appear in dropdowns immediately
          if (updatedUser.role === 'manager' && currentUser.role !== 'manager') {
            console.log('User role changed to manager, refreshing managers list...');
            try {
              const updatedManagersData = await getManagers();
              setManagers(updatedManagersData);
              console.log(`Managers list refreshed, now contains ${updatedManagersData.length} managers`);
            } catch (managerRefreshError) {
              console.error('Error refreshing managers list:', managerRefreshError);
              // Don't show error to user since the main operation succeeded
            }
          }
          // If the user's role was changed from manager to something else, also refresh the managers list
          else if (updatedUser.role !== 'manager' && currentUser.role === 'manager') {
            console.log('User role changed from manager, refreshing managers list...');
            try {
              const updatedManagersData = await getManagers();
              setManagers(updatedManagersData);
              console.log(`Managers list refreshed, now contains ${updatedManagersData.length} managers`);
            } catch (managerRefreshError) {
              console.error('Error refreshing managers list:', managerRefreshError);
              // Don't show error to user since the main operation succeeded
            }
          }

          // Show success toast
          toast.showToast(`User "${updatedUser.name}" has been updated successfully!`, { type: 'success' });

          // Reset form and close modal
          resetFormData();
          setShowEditModal(false);
        }
      } else {
        // Create new user
        const createData = mapDisplayUserToApiCreate({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          mobile: formData.mobile,
          storeTypeId: formData.storeTypeId,
          managerId: formData.managerId,
          canAccessAllStores: formData.canAccessAllStores
        }, managers);

        // Create API data using the helper function
        const apiData: Record<string, any> = {
          user_name: createData.user_name,
          email: createData.email,
          password: createData.password,
          mobile_number: createData.mobile_number,
          role: createData.role,
          store_type_id: createData.store_type_id,
          manager_id: createData.manager_id,
          can_access_all_stores: createData.can_access_all_stores
        };

        console.log('Sending user data:', JSON.stringify(apiData, null, 2));

        // Make API call to create user
        const newUserResponse = await apiClient.post<UserResponse>('/api/v1/users/', apiData);

        console.log('API response:', newUserResponse);

        if (newUserResponse) {
          let newUser: UserDisplay;
          try {
            newUser = mapApiUserToDisplayUser(newUserResponse);
          } catch (mappingError) {
            console.error('Error mapping new user:', mappingError);
            throw new Error('Error creating user: Failed to process server response');
          }

          // Add the new user to the users array
          setUsers([...users, newUser]);

          // Update total users count to ensure pagination appears
          setTotalUsers(prevTotal => prevTotal + 1);

          // If the new user is a manager, refresh the managers list so it appears in dropdowns immediately
          if (newUser.role === 'manager') {
            console.log('New manager created, refreshing managers list...');
            try {
              const updatedManagersData = await getManagers();
              setManagers(updatedManagersData);
              console.log(`Managers list refreshed, now contains ${updatedManagersData.length} managers`);
            } catch (managerRefreshError) {
              console.error('Error refreshing managers list:', managerRefreshError);
              // Don't show error to user since the main operation succeeded
            }
          }

          // Show success toast
          toast.showToast(`User "${newUser.name}" has been created successfully!`, { type: 'success' });

          // Reset form and close modal
          resetFormData();
          setShowAddModal(false);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      // Log the complete error object for debugging
      console.log('Complete error object:', JSON.stringify(error, null, 2));

      let errorMessage = 'Failed to save user. Please try again.';

      // Cast error to ApiError for type safety
      const apiError = error as ApiError;

      // Handle different types of errors
      if (apiError.response) {
        // Server responded with an error status
        console.log('Response status:', apiError.response.status);
        console.log('Response headers:', apiError.response.headers);
        console.log('Response data:', apiError.response.data);

        if (apiError.response.data) {
          // Handle validation errors (422 status)
          if (apiError.response.status === 422 && typeof apiError.response.data === 'object') {
            const validationErrors = [];

            // Check for structured validation errors
            if (apiError.response.data.detail && Array.isArray(apiError.response.data.detail)) {
              // FastAPI validation error format
              for (const detail of apiError.response.data.detail) {
                if (detail.msg && detail.loc) {
                  const field = Array.isArray(detail.loc) ? detail.loc.join('.') : detail.loc;
                  validationErrors.push(`${field}: ${detail.msg}`);
                } else if (detail.msg) {
                  validationErrors.push(detail.msg);
                }
              }
            }
            // Check for specific validation error patterns
            else if (apiError.response.data.msg) {
              validationErrors.push(apiError.response.data.msg);
            }
            // Check for password validation errors
            else if (apiError.response.data.password) {
              validationErrors.push(`Password: ${apiError.response.data.password}`);
            }
            // Check for email validation errors
            else if (apiError.response.data.email) {
              validationErrors.push(`Email: ${apiError.response.data.email}`);
            }
            // Check for mobile number validation errors
            else if (apiError.response.data.mobile_number) {
              validationErrors.push(`Mobile Number: ${apiError.response.data.mobile_number}`);
            }
            // Generic field errors
            else {
              Object.entries(apiError.response.data).forEach(([field, message]) => {
                if (field !== 'type' && field !== 'ctx' && message) {
                  if (Array.isArray(message)) {
                    validationErrors.push(`${field}: ${message.join(', ')}`);
                  } else {
                    validationErrors.push(`${field}: ${message}`);
                  }
                }
              });
            }

            if (validationErrors.length > 0) {
              errorMessage = validationErrors.join('\n');
            }
          }
          // Handle other structured error responses
          else if (typeof apiError.response.data === 'object') {
            if (apiError.response.data.detail) {
              errorMessage = apiError.response.data.detail;
            } else if (apiError.response.data.message) {
              errorMessage = apiError.response.data.message;
            } else {
              // Convert the error object to a readable string
              try {
                const errorDetails = Object.entries(apiError.response.data)
                  .filter(([key]) => !['type', 'ctx'].includes(key)) // Exclude technical fields
                  .map(([key, value]) => {
                    if (Array.isArray(value)) {
                      return `${key}: ${value.join(', ')}`;
                    }
                    return `${key}: ${value}`;
                  })
                  .join('\n');
                errorMessage = errorDetails || errorMessage;
              } catch (e) {
                console.error('Error parsing error details:', e);
              }
            }
          } else if (typeof apiError.response.data === 'string') {
            errorMessage = apiError.response.data;
          }
        }

        // Handle specific HTTP status codes
        if (apiError.response.status === 409) {
          errorMessage = 'Email address is already registered. Please use a different email address.';
        } else if (apiError.response.status === 400) {
          errorMessage = errorMessage || 'Invalid data provided. Please check your input and try again.';
        } else if (apiError.response.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (apiError.response.status === 404) {
          errorMessage = 'User not found. The user may have been deleted by another administrator.';
        } else if (apiError.response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (apiError.response.status >= 500) {
          errorMessage = 'Server error occurred. Please try again later or contact support if the problem persists.';
        }
      } else if (apiError.message && apiError.message !== '[object Object]') {
        // Network error or other error without response
        errorMessage = apiError.message;
      } else if (apiError.status === 0 || !apiError.status) {
        // Network connectivity issues
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        // Generic fallback for unknown error types
        errorMessage = 'An unexpected error occurred while saving the user. Please try again.';
      }

      setError(errorMessage);
      console.log('Detailed error:', error);

      // Show error toast for better visibility
      toast.showToast(errorMessage, { type: 'error' });

      // Don't close the modal if there's an error so user can see the error and fix their input
      // The form will remain open with their values still populated
    } finally {
      setIsLoading(false);
    }
  };

  // Update pagination handlers
  const handlePageChange = (pageNumber: number) => {
    console.log(`Changing page from ${currentPage} to ${pageNumber}`);
    
    // Set flags to prevent other functions from interfering
    setIsPageChanging(true);
    
    // Set current page state first to update UI
    setCurrentPage(pageNumber);
    
    // Set loading state immediately
    setIsLoading(true);
    
    // Use ref to get the latest itemsPerPage value to avoid closure issues
    const currentItemsPerPage = itemsPerPageRef.current;
    // Calculate skip based on new page number
    const skip = (pageNumber - 1) * currentItemsPerPage;
    const searchTerm = debouncedSearchQuery.trim();
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', currentItemsPerPage.toString());
    if (searchTerm) {
      queryParams.append('search', searchTerm);
    }
    
    // Log the URL being used
    const apiUrl = `${API_URL}${ENDPOINTS.USERS}?${queryParams.toString()}`;
    console.log(`Direct API call for page ${pageNumber}: ${apiUrl}`);
    
    // Get the auth token
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found in localStorage');
      setIsLoading(false);
      setError('Authentication token is missing');
      return;
    }
    
    // Make a direct API call without using state updates that might trigger useEffect
    fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Intercepted-Request': 'true',
        'Cache-Control': 'no-cache'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('Page change API response:', data);
      
      // Process the data
      if (data && data.items && Array.isArray(data.items)) {
        // Map the API response to our display format
        const displayUsers = data.items.map((item: any) => ({
          id: String(item.id || ''),
          name: String(item.user_name || ''),
          email: String(item.email || ''),
          role: String(item.role || 'staff'),
          status: item.is_active === false ? 'inactive' : 'active',
          mobile: String(item.mobile_number || ''),
          storeType: item.store_type?.name || '',
          storeTypeId: item.store_type?.id || null,
          manager: item.manager?.user_name || '',
          managerId: item.manager?.id || null,
        }));
        
        // Update all state at once to avoid race conditions
        setUsers(displayUsers);
        setTotalUsers(data.total || displayUsers.length);
        setTotalPages(data.pages || Math.ceil(data.total / currentItemsPerPage) || 1);
        // Don't set current page here as we already set it at the beginning
      } else {
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(1);
      }
    })
    .catch(error => {
      console.error('Error fetching users for page change:', error);
      setError(`Failed to load users: ${error.message}`);
    })
    .finally(() => {
      setIsLoading(false);
      setIsPageChanging(false);
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    console.log(`Changing items per page from ${itemsPerPage} to ${newItemsPerPage}`);
    
    // Set flags to prevent other functions from interfering
    setIsPageChanging(true);
    
    // Set loading state immediately
    setIsLoading(true);
    
    // Always reset to first page when changing items per page
    const pageNumber = 1;
    
    // Update both itemsPerPage and currentPage state BEFORE making the API call
    // This ensures any effects that might watch these values see the correct state
    // Also update the ref immediately to avoid closure issues
    itemsPerPageRef.current = newItemsPerPage;
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(pageNumber);
    
    // Calculate skip based on first page
    const skip = 0;
    const searchTerm = debouncedSearchQuery.trim();
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', newItemsPerPage.toString());
    if (searchTerm) {
      queryParams.append('search', searchTerm);
    }
    
    // Log the URL being used
    const apiUrl = `${API_URL}${ENDPOINTS.USERS}?${queryParams.toString()}`;
    console.log(`Direct API call for items per page change: ${apiUrl}`);
    
    // Get the auth token
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found in localStorage');
      setIsLoading(false);
      setIsPageChanging(false);
      setError('Authentication token is missing');
      return;
    }
    
    // Make a direct API call without using state updates that might trigger useEffect
    fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Intercepted-Request': 'true',
        'Cache-Control': 'no-cache'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('Items per page change API response:', data);
      
      // Process the data
      if (data && data.items && Array.isArray(data.items)) {
        // Map the API response to our display format
        const displayUsers = data.items.map((item: any) => ({
          id: String(item.id || ''),
          name: String(item.user_name || ''),
          email: String(item.email || ''),
          role: String(item.role || 'staff'),
          status: item.is_active === false ? 'inactive' : 'active',
          mobile: String(item.mobile_number || ''),
          storeType: item.store_type?.name || '',
          storeTypeId: item.store_type?.id || null,
          manager: item.manager?.user_name || '',
          managerId: item.manager?.id || null,
        }));
        
        // Update all state at once to avoid race conditions
        // Use the newItemsPerPage value directly to ensure consistency
        setUsers(displayUsers);
        setTotalUsers(data.total || displayUsers.length);
        setTotalPages(data.pages || Math.ceil(data.total / newItemsPerPage) || 1);
        // currentPage is already set above, no need to set it again
      } else {
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(1);
      }
    })
    .catch(error => {
      console.error('Error fetching users for items per page change:', error);
      setError(`Failed to load users: ${error.message}`);
    })
    .finally(() => {
      setIsLoading(false);
      setIsPageChanging(false);
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
  };

  // Helper function to render user row - memoized to respond to auth changes
  const renderUserRow = useCallback((user: UserDisplay): React.ReactElement | null => {
    // Check for valid user data
    if (!user || !user.id) {
      console.error('Invalid user data:', user);
      return null;
    }

    try {
      // Map API response fields to display fields with correct property names
      const name = user.name || user.user_name || 'N/A'; // Try both name and user_name
      const email = user.email || 'N/A';
      const mobile = user.mobile || user.mobile_number || 'N/A'; // Try both mobile and mobile_number
      const role = user.role || 'staff';
      const roleName = role.charAt(0).toUpperCase() + role.slice(1);

      // Properly extract manager information
      let manager = user.manager || 'N/A';

      // Correctly map store_type
      let storeType = 'N/A';
      if (user.store_type && user.store_type.name) {
        storeType = user.store_type.name;
      } else if (user.storeType) {
        storeType = user.storeType; // Fallback to storeType if store_type.name is not available
      }

      const isActive = user.is_active !== undefined ? user.is_active : (user.status === 'active');

      // Check if this is the current user's own account - this is the key fix
      const isOwnAccount = !!(currentAuthUser && currentAuthUser.id === user.id);

      // Debug log for delete button visibility
      if (currentAuthUser && currentAuthUser.id === user.id) {
        console.log(`Delete button disabled for own account: ${user.name} (${user.id})`);
      }

      return (
        <GridRow key={user.id}>
          <div title={name}>{name}</div>
          <div title={mobile}>{mobile}</div>
          <div title={email}>{email}</div>
          <div>
            <span style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: '500',
              backgroundColor:
                role === 'admin' ? '#e6f7ff' :
                  role === 'manager' ? '#f6ffed' :
                    '#fff7e6',
              color:
                role === 'admin' ? '#096dd9' :
                  role === 'manager' ? '#52c41a' :
                    '#fa8c16'
            }}>
              {roleName}
            </span>
          </div>
          <div title={manager}>{manager}</div>
          <div title={storeType}>{storeType}</div>
          <div>
            <span style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: '500',
              backgroundColor: isActive ? 'rgba(82, 196, 26, 0.1)' : 'rgba(255, 77, 79, 0.1)',
              color: isActive ? '#52c41a' : '#ff4d4f'
            }}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div style={{ gap: '6px', display: 'flex', justifyContent: 'center' }}>
            <ActionButton onClick={() => handleEditUser(user)}>
              <img src={UserEditIcon} alt="Edit" width="22" height="22" />
            </ActionButton>
            <ActionButton
              onClick={() => handleDeleteUser(user.id)}
              disabled={isOwnAccount}
              title={isOwnAccount ? "You cannot delete your own account" : "Delete user"}
              style={{
                opacity: isOwnAccount ? 0.5 : 1,
                cursor: isOwnAccount ? 'not-allowed' : 'pointer'
              }}
            >
              <img src={userDeleteIcon} alt="Delete" width="22" height="22" />
            </ActionButton>
          </div>
        </GridRow>
      );
    } catch (error) {
      console.error('Error rendering user row:', error, user);
      return null;
    }
  }, [currentAuthUser, handleEditUser, handleDeleteUser]); // Include currentAuthUser to respond to auth changes

  // Helper function to render empty state - memoized for consistency
  const renderEmptyState = useCallback(() => {
    return (
      <EmptyStateContainer>
        <EmptyStateIcon>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </EmptyStateIcon>
        <EmptyStateTitle>No Users Found</EmptyStateTitle>
        <EmptyStateDescription>
          {debouncedSearchQuery
            ? "No users match your search criteria. Try a different search term."
            : "There are no users in the system. Add a user to get started."
          }
        </EmptyStateDescription>
        {!debouncedSearchQuery && (
          <EmptyStateButton onClick={() => handleAddUser()}>
            Add User
          </EmptyStateButton>
        )}
      </EmptyStateContainer>
    );
  }, [debouncedSearchQuery, handleAddUser]);

  // Render grid content with consistent loading pattern - memoized to respond to auth changes
  const renderGridContent = useMemo(() => {
    // Debug log to track re-renders
    console.log('renderGridContent re-rendering due to dependency change:', {
      isLoading,
      usersCount: users.length,
      currentAuthUserId: currentAuthUser?.id || 'null'
    });

    if (isLoading) {
      return (
        <LoadingRow>
          <LoadingContainer>
            <LoadingSpinner size="lg" />
            <LoadingText>Loading users</LoadingText>
          </LoadingContainer>
        </LoadingRow>
      );
    }

    if (users && users.length > 0) {
    // Client-side filter fallback to ensure search works immediately after navigation
    const term = (debouncedSearchQuery || searchQuery || '').trim().toLowerCase();
    const base = term
      ? users.filter(u =>
          (u.name || u.user_name || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term) ||
          (u.role || '').toLowerCase().includes(term) ||
          (u.mobile || '').toLowerCase().includes(term)
        )
      : users;
    return base.map(user => renderUserRow(user)).filter(Boolean);
    }

    return renderEmptyState();
  }, [isLoading, users, currentAuthUser, renderUserRow, renderEmptyState]); // Include currentAuthUser to trigger re-render when auth state changes

  // Add these missing handler functions inside the UsersPage component, before the return statement
  const handleToggleUserStatus = (userId: string, active: boolean) => {
    // Show confirmation dialog for status change
    if (window.confirm(`Are you sure you want to ${active ? 'activate' : 'deactivate'} this user?`)) {
      // This would typically call an API to update the user status
      console.log(`Toggling status for user ${userId} to ${active ? 'active' : 'inactive'}`);

      // Update local state to reflect the change
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, is_active: active } : user
        )
      );
    }
  };

  const handleResetPassword = (userId: string) => {
    // Show confirmation dialog for password reset
    if (window.confirm('Are you sure you want to reset this user\'s password?')) {
      // This would typically call an API to reset the password
      console.log(`Resetting password for user ${userId}`);
      // You would then show a success message or error based on API response
    }
  };

  // Helper function to determine if store type field should be disabled
  const isStoreTypeFieldDisabled = (role: string): boolean => {
    // Disable store type field for staff role since it's auto-populated from manager
    // For manager and admin roles, the field should be enabled
    return isLoading || role === 'staff';
  };

  // Add a direct method to force update manager's store type
  const updateManagerStoreType = (managerId: string | null) => {
    if (!managerId || formData.role !== 'staff') return;

    // Find the manager
    const selectedManager = managers.find(m => m.id === managerId);
    if (!selectedManager) {
      console.warn(`Could not find manager with ID ${managerId}`);
      return;
    }

    console.log("Selected manager full object:", JSON.stringify(selectedManager, null, 2));

    // Try different ways to get store type id
    let storeTypeId: number | null = null;

    // First check for store_type object (new API format)
    if (selectedManager.store_type && selectedManager.store_type.id !== undefined) {
      storeTypeId = selectedManager.store_type.id;
      console.log(`Found store type ID ${storeTypeId} in store_type object`);
    }
    // Fallback to store_type_id if available (legacy format)
    else if (selectedManager.store_type_id !== undefined) {
      storeTypeId = selectedManager.store_type_id;
      console.log(`Found store type ID ${storeTypeId} from store_type_id property`);
    }

    // Only update if the store type is different from current value to prevent unnecessary rerenders
    if (storeTypeId !== null && storeTypeId !== formData.storeTypeId) {
      console.log(`Direct update: Setting store type to ${storeTypeId} from manager ${selectedManager.user_name}`);
      setFormData(prev => ({
        ...prev,
        storeTypeId: storeTypeId
      }));
    } else if (storeTypeId === null) {
      console.warn(`Manager ${selectedManager.user_name} has no store type information in any format`);
    } else {
      console.log(`Skipping update as store type ID ${storeTypeId} is already set`);
    }
  };

  // Helper function to determine if manager field should be disabled
  const isManagerFieldDisabled = (role: string): boolean => {
    // Only disable manager field for manager role, not for admin or staff
    return isLoading || role === 'manager';
  };

  // Helper function to check if the user being edited is the current logged-in user
  const isEditingOwnAccount = (): boolean => {
    return !!(currentUser && currentAuthUser && currentAuthUser.id === currentUser.id);
  };

  // Helper function to determine if email field should be disabled
  const isEmailFieldDisabled = (): boolean => {
    return isLoading || isEditingOwnAccount();
  };

  // Helper function to determine if password field should be disabled
  const isPasswordFieldDisabled = (): boolean => {
    return isLoading || isEditingOwnAccount();
  };

  // Helper function to determine if role field should be disabled
  const isRoleFieldDisabled = (): boolean => {
    return isLoading || isEditingOwnAccount();
  };

  // Helper function to determine if status field should be disabled
  const isStatusFieldDisabled = (): boolean => {
    return isLoading || isEditingOwnAccount();
  };

  // Helper function to determine if store type field should be disabled for own account
  const isStoreTypeFieldDisabledForOwnAccount = (): boolean => {
    // For manager role, store type should always be enabled (unless loading or editing own account)
    if (formData.role === 'manager') {
      return isLoading || isEditingOwnAccount();
    }
    // For staff role, disable since it's auto-populated from manager
    // For admin role, enable (optional field)
    return isLoading || isEditingOwnAccount() || isStoreTypeFieldDisabled(formData.role);
  };

  // Helper function to determine if manager field should be disabled for own account
  const isManagerFieldDisabledForOwnAccount = (): boolean => {
    return isLoading || isEditingOwnAccount() || isManagerFieldDisabled(formData.role);
  };

  // Memoized search input to prevent unnecessary re-renders
  const MemoizedSearchInput = useMemo(() => (
    <StyledSearchInput>
      <SearchInputComponent
        key="users-search-input"
        ref={searchInputRef}
        placeholder="Search Users"
        value={searchQuery}
        onChange={handleSearchChange}
        disabled={false}
      />
    </StyledSearchInput>
  ), [searchQuery, isLoading]);

  // Debug useEffect to track form data changes
  useEffect(() => {
    console.log('Form data changed:', {
      name: formData.name,
      email: formData.email,
      password: formData.password ? '*'.repeat(formData.password.length) : '',
      role: formData.role,
      mobile: formData.mobile,
      currentUser: currentUser?.id || 'null',
      showAddModal,
      showEditModal
    });
  }, [formData, currentUser, showAddModal, showEditModal]);

  // First useEffect for initial load
  useEffect(() => {
    // Prevent fetch if not authenticated
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // If navigated with ?search= from Global Search, prefill and bypass
    const params = new URLSearchParams(location.search);
    const initialSearch = params.get('search');
    if (initialSearch && initialSearch.trim().length > 0) {
      setSearchQuery(initialSearch);
      setDebouncedSearchQuery(initialSearch);
      setCurrentPage(1);
      // Force an immediate fetch that uses the provided search value
      (async () => {
        await fetchUsers({ overrideSearch: initialSearch });
      })();
    } else {
      // Load data on initial render
      fetchUsers();
    }

    // Only depend on authentication state to prevent unnecessary rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Second useEffect to handle search changes only
  useEffect(() => {
    // Keep URL in sync with search so refresh preserves it, but avoid router navigation
    // Using history.replaceState prevents remounts that blur the input while typing
    const params = new URLSearchParams(location.search);
    const current = params.get('search') || '';
    const trimmed = debouncedSearchQuery.trim();
    if (trimmed !== current) {
      const next = new URLSearchParams(location.search);
      if (trimmed) next.set('search', trimmed); else next.delete('search');
      const newUrl = `${location.pathname}${next.toString() ? `?${next.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    // Skip on initial render - the first useEffect handles that
    if (!dataLoaded) return;

    // Skip if loading is already in progress or page is changing
    if (isLoading || isPageChanging) return;

    console.log('Search useEffect triggered:', {
      searchQuery: debouncedSearchQuery.trim()
    });

    // Track previous search query to detect when search is cleared
    const hadSearchBefore = previousSearchRef.current.trim() !== '';

    // Only fetch for search changes with a delay
    if (debouncedSearchQuery.trim()) {
      const searchTimer = setTimeout(() => {
        // Reset to page 1 when search changes
        setCurrentPage(1);
        fetchUsers();
      }, 300);

      // Update previous search query
      previousSearchRef.current = debouncedSearchQuery;
      return () => clearTimeout(searchTimer);
    } else if (hadSearchBefore && !debouncedSearchQuery.trim()) {
      // Search was cleared - fetch all users and reset to page 1
      const searchTimer = setTimeout(() => {
        setCurrentPage(1);
        fetchUsers();
      }, 300);

      // Update previous search query
      previousSearchRef.current = debouncedSearchQuery;
      return () => clearTimeout(searchTimer);
    }
    
    // Update previous search query even if no action taken
    previousSearchRef.current = debouncedSearchQuery;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // React to changes to ?search= in the URL (e.g., from Global Search while already on this page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    // Only update if different from the current field value
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
      setDebouncedSearchQuery(urlSearch);
      setCurrentPage(1);
      // Trigger immediate fetch so results update without waiting for debounce
      fetchUsers();
    }
  }, [location.search]);

  // Add useEffect to check authToken on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    console.log('Auth token in localStorage:', token ? `${token.substring(0, 20)}...` : 'missing');
    
    // Check if API_URL is correctly configured
    console.log('API_URL:', API_URL);
    console.log('ENDPOINTS.USERS:', ENDPOINTS.USERS);
    console.log('Full API endpoint:', `${API_URL}${ENDPOINTS.USERS}`);
  }, []);

  // Calculate pagination values
  const totalItems = totalUsers;
  // const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  return (
    <Layout>
      <PageContainer>
        {/* Custom header with back button and title on a single line */}
        <CustomHeader>
          <BackButtonComponent onClick={handleBack} compact={true} />
          <PageTitle>Users Management</PageTitle>
        </CustomHeader>

        {/* Error message if present - exclude deletion errors as they show in modal */}
        {error && !error.includes('staff member') && !error.includes('reporting to them') && (
          <div style={{
            color: '#dc3545',
            backgroundColor: '#fff5f5',
            padding: '10px 15px',
            borderRadius: '6px',
            marginBottom: '15px',
            border: '1px solid #ffccd1',
            display: 'flex',
            alignItems: 'center'
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '10px' }}>
              <path d="M8 5V9M8 11.01L8.01 10.999M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {error}
          </div>
        )}

        {/* Search and Add User in one line */}
        <SearchActionContainer>
          {MemoizedSearchInput}
          <AddButtonComponent onClick={handleAddUser} disabled={isLoading} label="Add User" />
        </SearchActionContainer>

        <GridContainer>
          <GridHeader>
            <div>Name</div>
            <div>Mobile</div>
            <div>Email</div>
            <div>Role</div>
            <div>Manager</div>
            <div>Store Type</div>
            <div>Status</div>
            <div>Actions</div>
          </GridHeader>

          {renderGridContent}
        </GridContainer>

        {totalUsers > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalUsers}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
          />
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Add New User</ModalTitle>
              {error && (
                <ErrorMessage>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V9M8 11.01L8.01 10.999M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{error}</span>
                </ErrorMessage>
              )}
              <form key="add-user-form" onSubmit={handleSubmit}>
                <FormGrid>
                  <FormGroup>
                    <FormLabel>
                      Name<span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      placeholder="Enter name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Mobile Number<span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      placeholder="Enter mobile number"
                      onKeyPress={handleMobileKeyPress}
                    />
                    <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      Format: +61xxxxxxxx or 0xxxxxxxxx
                    </div>
                    {renderFieldErrors('mobile')}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Email<span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      placeholder="Enter email address"
                      autoComplete="new-email"
                    />
                    {renderFieldErrors('email')}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Password<span className="required">*</span>
                    </FormLabel>
                    <PasswordContainer>
                      <FormInput
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                            <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.708zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                          </svg>
                        )}
                      </PasswordToggle>
                    </PasswordContainer>
                    {renderFieldErrors('password')}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Role<span className="required">*</span>
                    </FormLabel>
                    <FormSelect
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    >
                      <option value="">Select a role</option>
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Store Type
                      {formData.role === 'manager' && <span className="required">*</span>}
                    </FormLabel>
                    <FormSelect
                      name="storeTypeId"
                      value={formData.storeTypeId || ''}
                      onChange={handleInputChange}
                      required={formData.role === 'manager'}
                      disabled={isStoreTypeFieldDisabledForOwnAccount() || loadingManagersAndStoreTypes}
                    >
                      <option value="">Select a store type</option>
                      {loadingManagersAndStoreTypes && (
                        <option value="" disabled>Loading store types</option>
                      )}
                      {!loadingManagersAndStoreTypes && storeTypes.length === 0 && (
                        <option value="" disabled>No store types available</option>
                      )}
                      {!loadingManagersAndStoreTypes && storeTypes.map(storeType => (
                        <option key={storeType.id} value={storeType.id}>
                          {storeType.name}
                        </option>
                      ))}
                    </FormSelect>
                    {formData.role === 'admin' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Store type is optional for Admin users.
                      </div>
                    )}
                    {formData.role === 'staff' && formData.managerId && (
                      <div style={{ color: '#4CAF50', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Store type is auto-populated from selected Manager.
                        {formData.storeTypeId && (
                          <span style={{ fontWeight: 'bold' }}>
                            {' '}{storeTypes.find(st => st.id === formData.storeTypeId)?.name || `ID: ${formData.storeTypeId}`}
                          </span>
                        )}
                        {!formData.storeTypeId && (
                          <span style={{ color: 'red', fontWeight: 'bold' }}>
                            {' '}No store type found for this manager!
                          </span>
                        )}
                      </div>
                    )}
                    {formData.role === 'staff' && !formData.managerId && (
                      <div style={{ color: '#FFA500', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Store type will be auto-populated once Manager is selected.
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Manager
                      {formData.role === 'staff' && <span className="required">*</span>}
                    </FormLabel>
                    <FormSelect
                      name="managerId"
                      value={formData.role === 'manager' ? '' : (formData.managerId !== null ? formData.managerId : '')}
                      onChange={(e) => {
                        // First call the regular handler
                        handleInputChange(e);

                        // Then immediately use the direct approach to update store type
                        if (formData.role === 'staff' && e.target.value) {
                          // Use setTimeout to ensure this runs after state is updated
                          setTimeout(() => {
                            updateManagerStoreType(e.target.value);
                          }, 10);
                        }
                      }}
                      required={formData.role === 'staff'}
                      disabled={isManagerFieldDisabledForOwnAccount() || loadingManagersAndStoreTypes}
                    >
                      <option value="">None</option>
                      {loadingManagersAndStoreTypes && (
                        <option value="" disabled>Loading managers</option>
                      )}
                      {!loadingManagersAndStoreTypes && managers.length === 0 && (
                        <option value="" disabled>No managers available</option>
                      )}
                      {!loadingManagersAndStoreTypes && managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.user_name}
                        </option>
                      ))}
                    </FormSelect>
                    {formData.role === 'manager' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Manager field is set to None when role is Manager
                      </div>
                    )}
                    {formData.role === 'admin' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Manager is optional for Admin users
                      </div>
                    )}
                    {formData.role === 'staff' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Manager is required for Staff users
                      </div>
                    )}
                  </FormGroup>
                </FormGrid>

                <ButtonGroup>
                  {formData.role === 'manager' && (
                    <CheckboxContainer>
                      <input
                        type="checkbox"
                        id="canAccessAllStores"
                        name="canAccessAllStores"
                        checked={formData.canAccessAllStores}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            canAccessAllStores: e.target.checked
                          }));
                        }}
                        disabled={isLoading}
                      />
                      <label htmlFor="canAccessAllStores">
                        Allow access to all stores
                      </label>
                    </CheckboxContainer>
                  )}
                  {formData.role !== 'manager' && <div></div>}
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <CancelButton
                      type="button"
                      onClick={() => {
                        resetFormData();
                        setShowAddModal(false);
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </CancelButton>
                    <SubmitButton
                      type="submit"
                      disabled={isLoading || loadingManagersAndStoreTypes}
                    >
                      {isLoading ? 'Saving' : loadingManagersAndStoreTypes ? 'Loading' : 'Add User'}
                    </SubmitButton>
                  </div>
                </ButtonGroup>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Edit User Modal */}
        {showEditModal && currentUser && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Edit User: {currentUser.name}</ModalTitle>
              {error && (
                <ErrorMessage>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V9M8 11.01L8.01 10.999M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{error}</span>
                </ErrorMessage>
              )}
              <form key={`edit-user-form-${currentUser?.id || 'new'}`} onSubmit={handleSubmit}>
                <FormGrid>
                  <FormGroup>
                    <FormLabel>
                      User Name<span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      placeholder="Enter user name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Mobile Number<span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                      placeholder="Enter mobile number"
                      onKeyPress={handleMobileKeyPress}
                    />
                    <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      Format: +61xxxxxxxx or 0xxxxxxxxx
                    </div>
                    {renderFieldErrors('mobile')}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Email<span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isEmailFieldDisabled()}
                      placeholder="Enter email address"
                      autoComplete="new-email"
                    />
                    {renderFieldErrors('email')}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Password (leave empty to keep current)
                    </FormLabel>
                    <PasswordContainer>
                      <FormInput
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        disabled={isPasswordFieldDisabled()}
                        placeholder="At least 8 characters"
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isPasswordFieldDisabled()}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                            <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.708zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                          </svg>
                        )}
                      </PasswordToggle>
                    </PasswordContainer>
                    {renderFieldErrors('password')}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Role<span className="required">*</span>
                    </FormLabel>
                    <FormSelect
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                      disabled={isRoleFieldDisabled()}
                    >
                      <option value="">Select a role</option>
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Status<span className="required">*</span>
                    </FormLabel>
                    <FormSelect
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      disabled={isStatusFieldDisabled()}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Store Type
                      {formData.role === 'manager' && <span className="required">*</span>}
                    </FormLabel>
                    <FormSelect
                      name="storeTypeId"
                      value={formData.storeTypeId || ''}
                      onChange={handleInputChange}
                      required={formData.role === 'manager'}
                      disabled={isStoreTypeFieldDisabledForOwnAccount() || loadingManagersAndStoreTypes}
                    >
                      <option value="">Select a store type</option>
                      {loadingManagersAndStoreTypes && (
                        <option value="" disabled>Loading store types</option>
                      )}
                      {!loadingManagersAndStoreTypes && storeTypes.length === 0 && (
                        <option value="" disabled>No store types available</option>
                      )}
                      {!loadingManagersAndStoreTypes && storeTypes.map(storeType => (
                        <option key={storeType.id} value={storeType.id}>
                          {storeType.name}
                        </option>
                      ))}
                    </FormSelect>
                    {formData.role === 'admin' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Store type is optional for Admin users.
                      </div>
                    )}
                    {formData.role === 'staff' && formData.managerId && (
                      <div style={{ color: '#4CAF50', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Store type is auto-populated from selected Manager.
                        {formData.storeTypeId && (
                          <span style={{ fontWeight: 'bold' }}>
                            {' '}{storeTypes.find(st => st.id === formData.storeTypeId)?.name || `ID: ${formData.storeTypeId}`}
                          </span>
                        )}
                        {!formData.storeTypeId && (
                          <span style={{ color: 'red', fontWeight: 'bold' }}>
                            {' '}No store type found for this manager!
                          </span>
                        )}
                      </div>
                    )}
                    {formData.role === 'staff' && !formData.managerId && (
                      <div style={{ color: '#FFA500', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Store type will be auto-populated once Manager is selected.
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Manager
                      {formData.role === 'staff' && <span className="required">*</span>}
                    </FormLabel>
                    <FormSelect
                      name="managerId"
                      value={formData.role === 'manager' ? '' : (formData.managerId !== null ? formData.managerId : '')}
                      onChange={(e) => {
                        // First call the regular handler
                        handleInputChange(e);

                        // Then immediately use the direct approach to update store type
                        if (formData.role === 'staff' && e.target.value) {
                          // Use setTimeout to ensure this runs after state is updated
                          setTimeout(() => {
                            updateManagerStoreType(e.target.value);
                          }, 10);
                        }
                      }}
                      required={formData.role === 'staff'}
                      disabled={isManagerFieldDisabledForOwnAccount() || loadingManagersAndStoreTypes}
                    >
                      <option value="">None</option>
                      {loadingManagersAndStoreTypes && (
                        <option value="" disabled>Loading managers</option>
                      )}
                      {!loadingManagersAndStoreTypes && managers.length === 0 && (
                        <option value="" disabled>No managers available</option>
                      )}
                      {!loadingManagersAndStoreTypes && managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.user_name}
                        </option>
                      ))}
                    </FormSelect>
                    {formData.role === 'manager' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Manager field is set to None when role is Manager
                      </div>
                    )}
                    {formData.role === 'admin' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Manager is optional for Admin users
                      </div>
                    )}
                    {formData.role === 'staff' && (
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Manager is required for Staff users
                      </div>
                    )}
                  </FormGroup>
                </FormGrid>

                <ButtonGroup>
                  {formData.role === 'manager' && (
                    <CheckboxContainer>
                      <input
                        type="checkbox"
                        id="canAccessAllStoresEdit"
                        name="canAccessAllStores"
                        checked={formData.canAccessAllStores}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            canAccessAllStores: e.target.checked
                          }));
                        }}
                        disabled={isLoading || isRoleFieldDisabled()}
                      />
                      <label htmlFor="canAccessAllStoresEdit">
                        Allow access to all stores
                      </label>
                    </CheckboxContainer>
                  )}
                  {formData.role !== 'manager' && <div></div>}
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <CancelButton
                      type="button"
                      onClick={() => {
                        resetFormData();
                        setShowEditModal(false);
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </CancelButton>
                    <SubmitButton
                      type="submit"
                      disabled={isLoading || loadingManagersAndStoreTypes || !hasFormChanges}
                    >
                      {isLoading ? 'Saving' : loadingManagersAndStoreTypes ? 'Loading' : 'Update User'}
                    </SubmitButton>
                  </div>
                </ButtonGroup>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && userToDelete && (
          <ModalOverlay onClick={() => setShowDeleteModal(false)}>
            <DeleteModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Delete User</ModalTitle>
              <DeleteModalText>
                Are you sure you want to delete user "{userToDelete.name}"? This action cannot be undone.
              </DeleteModalText>
              <ButtonGroup>
                <CancelButton onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </CancelButton>
                <DeleteButton onClick={handleConfirmDelete} disabled={isLoading}>
                  {isLoading ? 'Deleting...' : 'Delete User'}
                </DeleteButton>
              </ButtonGroup>
            </DeleteModalContent>
          </ModalOverlay>
        )}

        {/* Delete Error Modal */}
        {showDeleteErrorModal && (
          <ModalOverlay onClick={() => setShowDeleteErrorModal(false)}>
            <DeleteModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Cannot Delete User</ModalTitle>
              <DeleteModalText>
                {deleteErrorMessage}
              </DeleteModalText>
              <ButtonGroup>
                <SubmitButton onClick={() => setShowDeleteErrorModal(false)}>
                  OK
                </SubmitButton>
              </ButtonGroup>
            </DeleteModalContent>
          </ModalOverlay>
        )}

        {toast.isVisible && <Toast message={toast.message} type={toast.type} onClose={() => {}} />}
      </PageContainer>
    </Layout>
  );
};

export default UsersPage;
